import PropTypes from 'prop-types';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import { formatUkPhone } from 'src/utils/ukPhone';
import { SUGGESTION_REASON_TEXT } from './callFlowModel';

// One lead the call could belong to, with why it was suggested (small chips) and
// the link buttons. An archived lead offers "Unarchive and link" / "Link, keep
// archived", the same two choices as the caller journeys on a Call Logs row.
export function LeadMatchRow({ entry, busy, confirmLabel, onChoose }) {
    const { lead, reasons } = entry;
    return (
        <Box data-testid={`lead-match-${lead.id}`} sx={{ p: 1.25, borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" useFlexGap>
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" noWrap>{lead.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                        {[lead.phone ? formatUkPhone(lead.phone) : '', lead.email].filter(Boolean).join(' · ') || 'No phone or email'}
                    </Typography>
                    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
                        {lead.isArchived && <Chip size="small" color="warning" variant="soft" label={lead.archiveReason ? `Archived · ${lead.archiveReason}` : 'Archived'} />}
                        {reasons.filter((r) => r !== 'archived').map((r) => <Chip key={r} size="small" variant="soft" label={SUGGESTION_REASON_TEXT[r]} />)}
                    </Stack>
                </Box>
                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                    {lead.isArchived ? (
                        <>
                            <Button size="small" variant="contained" disabled={busy} onClick={() => onChoose({ lead, unarchive: true })}>Unarchive and link</Button>
                            <Button size="small" variant="outlined" disabled={busy} onClick={() => onChoose({ lead, unarchive: false })}>Link call, keep patient archived</Button>
                        </>
                    ) : (
                        <Button size="small" variant="contained" disabled={busy} onClick={() => onChoose({ lead, unarchive: false })}>{confirmLabel}</Button>
                    )}
                </Stack>
            </Stack>
        </Box>
    );
}

const leadShape = PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    name: PropTypes.string.isRequired,
    phone: PropTypes.string,
    email: PropTypes.string,
    isArchived: PropTypes.bool,
    archiveReason: PropTypes.string,
});
const entryShape = PropTypes.shape({ lead: leadShape.isRequired, reasons: PropTypes.arrayOf(PropTypes.string).isRequired });

LeadMatchRow.propTypes = { entry: entryShape.isRequired, busy: PropTypes.bool, confirmLabel: PropTypes.string, onChoose: PropTypes.func.isRequired };
LeadMatchRow.defaultProps = { busy: false, confirmLabel: 'Link' };

export default function LeadMatchList({ entries, busy, confirmLabel, onChoose }) {
    return (
        <Stack spacing={1}>
            {entries.map((entry) => <LeadMatchRow key={entry.lead.id} entry={entry} busy={busy} confirmLabel={confirmLabel} onChoose={onChoose} />)}
        </Stack>
    );
}

LeadMatchList.propTypes = { entries: PropTypes.arrayOf(entryShape).isRequired, busy: PropTypes.bool, confirmLabel: PropTypes.string, onChoose: PropTypes.func.isRequired };
LeadMatchList.defaultProps = { busy: false, confirmLabel: 'Link' };
